import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import AdminButton from '../components/AdminButton';
import CustomDateTimePicker from '../../../components/CustomDateTimePicker';
import { getFormattedDate, getTimeFromDate } from '../../../utils/dateUtils';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { useTenant } from '../../../contexts/TenantContext';
import { Routes } from '../../../routes/RouteConstants';
import useBlockPeriod from '../api/useBlockPeriod';
import AdminPage from '../layout/AdminPage';
import { useStaff } from '../StaffContext';
import ProviderSelect from '../components/ProviderSelect';
import {
  defaultEndFor,
  disabledTimeFor,
  firstAllowedStart,
  isAllowedTime,
  isClosedDay,
} from '../utils/openingHoursPicker';

/**
 * Closing off a period by hand — a holiday, a day off. It writes BLOCKED rows; each shows on the
 * calendar as „NINCS VENDÉGFOGADÁS” and is lifted from there. Until D32's phase 2 this was the
 * `BlockTimeModal` under the calendar.
 *
 * The pickers offer only the tenant's opening hours, on its slot grid (owner, 2026-09-24): a block
 * outside them would close off nothing a guest could book. The backend does not enforce this —
 * `blockPeriod` is an admin path (DECISIONS.md D2) — so it is the form's rule, not the system's.
 *
 * A block is one provider's, the one the calendar shows; there is no tenant-wide block. An admin of a
 * tenant with several providers picks whose here, the same choice as on the calendar
 * (docs/MULTI_PROVIDER.md).
 */
const BlockPeriodPage = () => {
  const { openingHours, timeSlotDuration } = useTenant();
  // An older backend sends neither: the pickers then restrict nothing, as they used to.
  const isRestricted = openingHours.length > 0;
  const step = timeSlotDuration || 1;

  const [startDate, setStartDate] = useState(() =>
    isRestricted ? firstAllowedStart(openingHours, step, dayjs()) : dayjs().add(1, 'hour').startOf('hour'));
  const [endDate, setEndDate] = useState(() =>
    isRestricted ? defaultEndFor(startDate, openingHours, step) : startDate.add(1, 'hour'));
  const { blockPeriod, loading } = useBlockPeriod();
  const { shownProviderId } = useStaff();
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  // A start moved past the end takes the end along, an hour on, rather than leaving an empty period.
  const changeStart = (value) => {
    setStartDate(value);
    if (value && endDate && !value.isBefore(endDate)) {
      setEndDate(isRestricted ? defaultEndFor(value, openingHours, step) : value.add(1, 'hour'));
    }
  };

  const pickerProps = (edge) => (isRestricted
    ? {
        shouldDisableDate: isClosedDay(openingHours),
        shouldDisableTime: disabledTimeFor(openingHours, step, edge),
        // Only the times that can be picked are listed; the grid counts from each day's opening.
        skipDisabled: true,
        timeSteps: { minutes: 1 },
      }
    : {});

  const validationError = () => {
    if (!startDate?.isValid() || !endDate?.isValid()) return 'Add meg az időszak kezdetét és végét.';
    if (isRestricted && !isAllowedTime(startDate, openingHours, step, 'start')) {
      return 'A kezdés a nyitvatartási időn kívül esik.';
    }
    if (isRestricted && !isAllowedTime(endDate, openingHours, step, 'end')) {
      return 'Az időszak vége a nyitvatartási időn kívül esik.';
    }
    if (!startDate.isBefore(endDate)) return 'Az időszak kezdete legyen a vége előtt.';
    return null;
  };

  const handleSubmit = async () => {
    const error = validationError();
    if (error) {
      showSnackbar(error, 'error');
      return;
    }

    const success = await blockPeriod({
      startDate: getFormattedDate(startDate),
      endDate: getFormattedDate(endDate),
      startTime: getTimeFromDate(startDate),
      endTime: getTimeFromDate(endDate),
      providerId: shownProviderId,
    });

    // Back to the calendar, where the new block is on the board.
    if (success) {
      showSnackbar('Szabadnap rögzítve', 'success');
      navigate(Routes.DASHBOARD);
    }
  };

  return (
    <AdminPage
      narrow
      title="Inaktív időszak beállítása"
      subtitle="A megadott időszakban a vendégek nem tudnak foglalni. A naptárban „NINCS VENDÉGFOGADÁS” blokként jelenik meg, és onnan oldható fel."
    >
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <ProviderSelect />
          <CustomDateTimePicker label="Mettől" date={startDate} setDate={changeStart} {...pickerProps('start')} />
          <CustomDateTimePicker label="Meddig" date={endDate} setDate={setEndDate} {...pickerProps('end')} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Csak nyitvatartási időpont választható. Több napos időszaknál a köztes napok egész
            nyitvatartása letiltódik, a zárva tartott napokra pedig nem kerül bejegyzés.
          </Typography>
          {/* Full width: the button centres itself, which stood it apart from the fields above. */}
          <AdminButton text="Rögzítés" onClick={handleSubmit} isLoading={loading} width="100%" />
        </Stack>
      </Paper>
    </AdminPage>
  );
};

export default BlockPeriodPage;
